
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { subDays, format } from 'date-fns';
import type { TimeRangeOption } from '@/types/analytics';

export { type TimeRangeOption };

export interface ProductClick {
  id: string;
  product_name: string;
  category: string | null;
  clicks: number;
  previous_clicks?: number;
  growth?: number;
}

export interface CategoryClick {
  category: string;
  clicks: number;
  previous_clicks?: number;
  growth?: number;
}

export interface DateClick {
  date: string;
  clicks: number;
  previous_clicks?: number;
  growth?: number;
}

// Export these for backward compatibility
export type ClicksByProduct = ProductClick;
export type ClicksByCategory = CategoryClick;
export type ClicksByDate = DateClick;

export const useProductAnalytics = (initialTimeRange: TimeRangeOption = '7days') => {
  const [timeRange, setTimeRange] = useState<TimeRangeOption>(initialTimeRange);
  const [loading, setLoading] = useState(true);
  const [clicksByDate, setClicksByDate] = useState<DateClick[]>([]);
  const [clicksByCategory, setClicksByCategory] = useState<CategoryClick[]>([]);
  const [clicksByProduct, setClicksByProduct] = useState<ProductClick[]>([]);
  const [totalClicks, setTotalClicks] = useState(0);
  const [previousTotalClicks, setPreviousTotalClicks] = useState(0);
  const [clicksGrowth, setClicksGrowth] = useState(0);

  const getDateRange = (range: TimeRangeOption) => {
    const now = new Date();
    let days: number;
    
    switch (range) {
      case '24h':
        days = 1;
        break;
      case '7days':
        days = 7;
        break;
      case '30days':
        days = 30;
        break;
      case '90days':
        days = 90;
        break;
      default:
        days = 7;
    }
    
    return {
      startDate: subDays(now, days),
      endDate: now,
      previousStartDate: subDays(now, days * 2),
      previousEndDate: subDays(now, days)
    };
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const { startDate, endDate, previousStartDate, previousEndDate } = getDateRange(timeRange);

      // Fetch current period analytics
      const { data: currentData, error: currentError } = await supabase
        .from('product_clicks')
        .select('*')
        .gte('clicked_at', startDate.toISOString())
        .lte('clicked_at', endDate.toISOString());

      if (currentError) {
        console.error("Error fetching current analytics:", currentError);
        setLoading(false);
        return;
      }

      // Fetch previous period analytics
      const { data: previousData, error: previousError } = await supabase
        .from('product_clicks')
        .select('*')
        .gte('clicked_at', previousStartDate.toISOString())
        .lte('clicked_at', previousEndDate.toISOString());

      if (previousError) {
        console.error("Error fetching previous analytics:", previousError);
        setLoading(false);
        return;
      }

      // Process current period data
      const dailyClicksMap: { [date: string]: number } = {};
      const categoryClicksMap: { [category: string]: number } = {};
      const productClicksMap: { [productName: string]: { id: string, product_name: string, category: string | null, clicks: number } } = {};

      let currentTotalClicks = 0;

      currentData?.forEach(click => {
        const formattedDate = format(new Date(click.clicked_at), 'yyyy-MM-dd');
        dailyClicksMap[formattedDate] = (dailyClicksMap[formattedDate] || 0) + 1;

        if (click.category) {
          categoryClicksMap[click.category] = (categoryClicksMap[click.category] || 0) + 1;
        }

        currentTotalClicks++;

        if (productClicksMap[click.product_name]) {
          productClicksMap[click.product_name].clicks++;
        } else {
          productClicksMap[click.product_name] = {
            id: click.id,
            product_name: click.product_name,
            category: click.category,
            clicks: 1
          };
        }
      });

      // Process previous period data to calculate growth
      let prevTotalClicks = 0;
      previousData?.forEach(() => {
        prevTotalClicks++;
      });

      const dailyClicks: DateClick[] = Object.keys(dailyClicksMap).map(date => ({
        date,
        clicks: dailyClicksMap[date]
      })).sort((a, b) => a.date.localeCompare(b.date));

      const categoryClicks: CategoryClick[] = Object.keys(categoryClicksMap).map(category => ({
        category,
        clicks: categoryClicksMap[category]
      })).sort((a, b) => b.clicks - a.clicks);

      const productClicks: ProductClick[] = Object.values(productClicksMap).sort((a, b) => b.clicks - a.clicks);

      // Calculate click growth
      const growth = prevTotalClicks === 0 ? 100 : ((currentTotalClicks - prevTotalClicks) / prevTotalClicks) * 100;

      setClicksByDate(dailyClicks);
      setClicksByCategory(categoryClicks);
      setClicksByProduct(productClicks);
      setTotalClicks(currentTotalClicks);
      setPreviousTotalClicks(prevTotalClicks);
      setClicksGrowth(growth);

    } catch (error) {
      console.error("Analytics fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  return {
    loading,
    clicksByDate,
    clicksByCategory,
    clicksByProduct,
    totalClicks,
    previousTotalClicks,
    clicksGrowth,
    timeRange,
    setTimeRange
  };
};
